import React, { useEffect, useState } from 'react';
import { TableContainer, Paper, Table, TableBody, TableRow, TableCell, styled, Typography, Button, useTheme, Box, Chip, Tooltip, Alert } from "@mui/material";
import { DateTime } from "luxon";
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { queryEmployeeFromStateOrDb } from '../../utils/queryEmployeeFromStateOrDb';
import { Employee } from '../../types/Employee';
import { ApartmentRounded, DoorFrontRounded, HomeRounded } from '@mui/icons-material';
import { EmployeePageContext } from './EmployeePage';

const BUILDINGS = ['A1', 'A2', 'A3', 'A4', 'A5', 'A6', 'A7', 'A8', 'A9', 'A10', 'A11'];
const BUILDINGS_VAL = ['A1', 'A2', 'A3', 'A4', 'B1', 'B2', 'C', 'D1', 'D2', 'E', 'A11'];
const BUILDINGS_WITH_DOOR = ['A1', 'A2', 'A3', 'A4', 'A11'];

const getBuildingByValue = (val: string) => {
  const idx = BUILDINGS_VAL.findIndex(v => v === val);
  return idx == -1 ? val : BUILDINGS[idx];
};

const EnhancedTableCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 700,
  width: 250,
}));

interface InfoBoxProps {};
export const InfoBox = (props: InfoBoxProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { employee }: EmployeePageContext = useOutletContext();

  const supervisorOnClick = (idNumber: string) => {
    navigate(`/home/employees/${idNumber}`);
  };

  if (!employee) {
    return (
      <Box>
        <Alert severity='info'>
          Getting employee information...
        </Alert>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableBody>
          <TableRow>
            <EnhancedTableCell>Name</EnhancedTableCell>
            <TableCell>{employee.name}</TableCell>
          </TableRow>
          <TableRow>
            <EnhancedTableCell component="th">ID Number</EnhancedTableCell>
            <TableCell>{employee.idNumber}</TableCell>
          </TableRow>
          <TableRow>
            <EnhancedTableCell component="th">Iqama Number</EnhancedTableCell>
            <TableCell>{employee.iqamaNumber}</TableCell>
          </TableRow>
          <TableRow>
            <EnhancedTableCell component="th">Passport Number</EnhancedTableCell>
            <TableCell>{employee.passportNumber}</TableCell>
          </TableRow>
          <TableRow>
            <EnhancedTableCell component="th">Department</EnhancedTableCell>
            <TableCell>{employee.department}</TableCell>
          </TableRow>
          <TableRow>
            <EnhancedTableCell component="th">Phone Number</EnhancedTableCell>
            <TableCell>{employee.phoneNumber}</TableCell>
          </TableRow>
          <TableRow>
            <EnhancedTableCell component="th">Occupation</EnhancedTableCell>
            <TableCell>{employee.occupation}</TableCell>
          </TableRow>
          <TableRow>
            <EnhancedTableCell component="th">Date Hired</EnhancedTableCell>
            <TableCell>{DateTime.fromFormat(employee.dateHired, 'yyyyMMdd').toFormat('LLLL dd, yyyy')}</TableCell>
          </TableRow>
          <TableRow>
            <EnhancedTableCell component="th">Lodging</EnhancedTableCell>
            <TableCell>
              <Box sx={{ display: 'flex', flexDirection: 'row', }}>
                <Tooltip title="Building">
                  <Chip sx={{ mr: 1 }} icon={<ApartmentRounded />} label={employee.lodge?.building ? getBuildingByValue(employee.lodge.building) : ''} />
                </Tooltip>
                <Tooltip title="Room Number">
                  <Chip sx={{ mr: 1 }} icon={<HomeRounded />} label={employee.lodge?.room ? employee.lodge.room : ''} />
                </Tooltip>
                {employee.lodge?.building && BUILDINGS_WITH_DOOR.includes(employee.lodge.building) && (
                  <Tooltip title="Door">
                    <Chip sx={{ mr: 1 }} icon={<DoorFrontRounded />} label={employee.lodge?.door ? employee.lodge.door : ''} />
                  </Tooltip>
                )}
              </Box>
            </TableCell>
          </TableRow>
          <TableRow>
            <EnhancedTableCell component="th">Supervisor</EnhancedTableCell>
            <TableCell>
              {employee.supervisorData !== undefined ? (
                <Button onClick={() => supervisorOnClick(employee.supervisorData!.idNumber)}>
                  <Typography variant="caption" sx={{
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.info.main,
                    marginBottom: 0,
                    lineHeight: 1,
                    padding: 1,
                    paddingTop: 0.5,
                    paddingBottom: 0.5,
                    marginRight: 1,
                    borderRadius: 1,
                  }}>
                    {employee.supervisorData.idNumber}
                  </Typography>
                  {employee.supervisorData.name}
                </Button>
              ) : (
                employee.supervisor
              )}
            </TableCell>
          </TableRow>
          <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
            <EnhancedTableCell component="th">Chief Supervisor</EnhancedTableCell>
            <TableCell>
              {employee.chiefSupervisorData !== undefined ? (
                <Button onClick={() => supervisorOnClick(employee.chiefSupervisorData!.idNumber)}>
                  <Typography variant="caption" sx={{
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.info.main,
                    marginBottom: 0,
                    lineHeight: 1,
                    padding: 1,
                    paddingTop: 0.5,
                    paddingBottom: 0.5,
                    marginRight: 1,
                    borderRadius: 1,
                   }}>
                    {employee.chiefSupervisorData.idNumber}
                  </Typography>
                  {employee.chiefSupervisorData.name}
                </Button>
              ) : (
                employee.chiefSupervisor
              )}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {/* <pre>
      {JSON.stringify(employee, null, 4)}
      </pre> */}
    </TableContainer>
  );
};
